import { inject, Injectable, NgZone } from '@angular/core';
import { User as FirebaseUser } from 'firebase/auth';
import { DocumentData, QueryConstraint } from 'firebase/firestore';
import { Observable } from 'rxjs';
import {
  FIREBASE_AUTH,
  FIREBASE_FIRESTORE,
  FIREBASE_SDK_OPERATIONS
} from './firebase.tokens';

export type FirebaseValueConstraint =
  | {
      readonly kind: 'where';
      readonly field: string;
      readonly operation: '==' | '>=';
      readonly value: unknown;
    }
  | {
      readonly kind: 'orderBy';
      readonly field: string;
      readonly direction: 'asc' | 'desc';
    }
  | { readonly kind: 'limit'; readonly count: number };

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly firestore = inject(FIREBASE_FIRESTORE);
  private readonly operations = inject(FIREBASE_SDK_OPERATIONS);
  private readonly zone = inject(NgZone);

  authenticate(token: string): Observable<FirebaseUser> {
    return new Observable(subscriber => {
      let active = true;
      this.operations.signInWithCustomToken(this.auth, token).then(
        credential => {
          if (active) {
            this.zone.run(() => {
              subscriber.next(credential.user);
              subscriber.complete();
            });
          }
        },
        error => {
          if (active) {
            this.zone.run(() => subscriber.error(error));
          }
        }
      );

      return () => {
        active = false;
      };
    });
  }

  valueChanges<T>(
    path: string,
    constraints: readonly FirebaseValueConstraint[]
  ): Observable<T[]> {
    return new Observable(subscriber => {
      const source = this.operations.collection(this.firestore, path);
      const queryConstraints = constraints.map(constraint =>
        this.toQueryConstraint(constraint)
      );
      const firebaseQuery = this.operations.query(source, queryConstraints);
      const unsubscribe = this.operations.onSnapshot(
        firebaseQuery,
        snapshot =>
          this.zone.run(() =>
            subscriber.next(snapshot.docs.map(document => document.data() as T))
          ),
        error => this.zone.run(() => subscriber.error(error))
      );

      return unsubscribe;
    });
  }

  private toQueryConstraint(
    constraint: FirebaseValueConstraint
  ): QueryConstraint {
    if (constraint.kind === 'where') {
      return this.operations.where(
        constraint.field,
        constraint.operation,
        constraint.value
      );
    }
    if (constraint.kind === 'orderBy') {
      return this.operations.orderBy(constraint.field, constraint.direction);
    }
    return this.operations.limit(constraint.count);
  }
}
